// Catat error submit form & error klien yang ga ketangkep ke Firestore `error_logs`.
// Sengaja lewat REST + sendBeacon, bukan Firestore SDK: beacon tetap terkirim walau
// halaman langsung redirect (403 -> /login) dan ga pernah nahan Swal waktu offline.
// Skema, rules & cara baca log: FIREBASE_SETUP.md.

// Payload dicatat apa adanya biar root cause kelihatan; yang dibuang cuma kredensial.
const DROP = /pass|pwd|token|secret|authorization|otp/i;

// Slice bisa motong pas di tengah pasangan surrogate emoji, nyisain high surrogate nyangkut sendirian
// di ujung — Firestore bisa nolak itu dan dokumennya ilang diam-diam. Buang kalau ada.
const cut = (v, n) => (v == null ? null : String(v).slice(0, n).replace(/[\ud800-\udbff]$/, ""));

// `players[3][player_nisn]` -> `player_nisn`
const lastSegment = (key) => String(key).match(/\[([^\]]*)\]$/)?.[1] ?? String(key);

const clean = (key, v) => {
  if (v instanceof Blob) return { name: v.name, size: v.size, type: v.type };
  if (v == null) return v;
  if (DROP.test(lastSegment(key))) return "[REDACTED]";
  if (Array.isArray(v)) return v.map((x) => clean(key, x));
  if (typeof v === "object") return walk(v);
  return typeof v === "string" ? v.slice(0, 200) : v;
};

const walk = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, clean(k, v)]));

// config.data dari axios: FormData asli, string JSON (objek udah di-stringify axios), atau undefined.
export function serializePayload(data) {
  if (data == null) return { payload: null, payloadBytes: 0, fileCount: 0 };

  if (data instanceof FormData) {
    const payload = {};
    let payloadBytes = 0;
    let fileCount = 0;
    for (const [k, v] of data.entries()) {
      payload[k] = clean(k, v); // ponytail: key duplikat ketimpa; form kita selalu pakai index eksplisit
      if (v instanceof Blob) {
        fileCount++;
        payloadBytes += v.size;
      } else {
        payloadBytes += String(v).length;
      }
    }
    return { payload, payloadBytes, fileCount };
  }

  const text = typeof data === "string" ? data : JSON.stringify(data);
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {}
  if (!parsed || typeof parsed !== "object") {
    return { payload: { unparsed: true, length: text.length }, payloadBytes: text.length, fileCount: 0 };
  }
  return { payload: walk(parsed), payloadBytes: text.length, fileCount: 0 };
}

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const readUser = () => {
  try {
    const state = JSON.parse(localStorage.getItem("user-data"))?.state;
    return {
      registrationId: cut(state?.registrationData?.id, 50),
      email: cut(state?.email || null, 100),
    };
  } catch {
    return { registrationId: null, email: null };
  }
};

let sid;
const sessionId = () => {
  if (sid) return sid;
  try {
    sid = sessionStorage.getItem("errlog-sid");
  } catch {}
  if (!sid) {
    sid = Math.random().toString(36).slice(2, 12);
    try {
      sessionStorage.setItem("errlog-sid", sid);
    } catch {}
  }
  return sid;
};

// Satu dokumen `error_logs`. Key-nya harus sama persis dengan allowlist di Firestore rules.
export function buildDoc(err, source) {
  const http = err?.isAxiosError ? err : null;
  const res = http?.response;
  const body = http ? serializePayload(http.config?.data) : null;
  const user = readUser();
  const now = new Date();

  return {
    timestamp: now,
    env: cut(window.location.host, 100),
    source,
    page: cut(window.location.pathname + window.location.search, 1000),
    message: cut(err?.message ?? err, 500),
    stack: http ? null : cut(err?.stack, 4000),
    method: http ? cut(http.config?.method?.toUpperCase(), 10) : null,
    url: http ? cut(http.config?.url, 500) : null,
    status: http ? (res?.status ?? 0) : null,
    code: http ? cut(http.code, 50) : null,
    response: res ? cut(typeof res.data === "string" ? res.data : JSON.stringify(res.data), 4000) : null,
    payload: body?.payload ? cut(JSON.stringify(body.payload), 8000) : null,
    payloadBytes: body ? body.payloadBytes : null,
    fileCount: body ? body.fileCount : null,
    sessionId: sessionId(),
    registrationId: user.registrationId,
    email: user.email,
    ua: cut(navigator.userAgent, 300),
    online: navigator.onLine,
    expireAt: new Date(now.getTime() + THIRTY_DAYS), // field TTL: dokumen dihapus setelah waktu ini lewat
  };
}

const toValue = (v) => {
  if (v == null) return { nullValue: null };
  if (v instanceof Date) return { timestampValue: v.toISOString() };
  if (typeof v === "boolean") return { booleanValue: v };
  if (typeof v === "number") return { integerValue: String(Math.trunc(v)) };
  return { stringValue: String(v) };
};

// Body Firestore REST createDocument.
export const encode = (doc) =>
  JSON.stringify({ fields: Object.fromEntries(Object.entries(doc).map(([k, v]) => [k, toValue(v)])) });

const ENDPOINT = `https://firestore.googleapis.com/v1/projects/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/databases/(default)/documents/error_logs`;
const QUEUE_KEY = "errlog-queue";
const MAX_QUEUE = 10;
const MAX_PER_LOAD = 20;

// Baca antrean dari localStorage: kalau rusak, hapus kunci & kembalikan array kosong.
const readQueue = () => {
  try {
    const data = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
    if (Array.isArray(data)) return data;
    // data bukan array: hapus kunci yg rusak
    try {
      localStorage.removeItem(QUEUE_KEY);
    } catch {}
    return [];
  } catch {
    // parse gagal: hapus kunci yg rusak
    try {
      localStorage.removeItem(QUEUE_KEY);
    } catch {}
    return [];
  }
};

const enqueue = (body) => {
  try {
    const queue = readQueue();
    localStorage.setItem(QUEUE_KEY, JSON.stringify([...queue, body].slice(-MAX_QUEUE)));
  } catch {}
};

const send = (body) => {
  if (navigator.onLine === false) return enqueue(body);
  // Body string = text/plain -> CORS simple request tanpa preflight; Firestore tetap baca sebagai JSON.
  // sendBeacon false biasanya kuota keepalive 64 KB lagi penuh, jadi fallback-nya fetch biasa.
  if (!navigator.sendBeacon?.(ENDPOINT, body)) {
    fetch(ENDPOINT, { method: "POST", body }).catch(() => {});
  }
};

export function flushQueue() {
  try {
    if (navigator.onLine === false) return;
    const queue = readQueue();
    try {
      localStorage.removeItem(QUEUE_KEY);
    } catch {}
    queue.forEach((body) => send(body));
  } catch {}
}

const seen = new Set();

// Fire-and-forget: ga pernah di-await, ga pernah melempar error ke pemanggil.
export default function logClientError(err, source) {
  try {
    if (process.env.NODE_ENV !== "production") return;
    const doc = buildDoc(err, source);
    // response ikut masuk key: dua 422 di URL yang sama bisa beda body ("email taken" vs "npsn taken")
    // tapi message-nya generik dari axios, jadi tanpa ini keduanya ketiban jadi satu log doang.
    const key = [doc.source, doc.method, doc.url, doc.status, doc.message?.slice(0, 80), doc.response?.slice(0, 200)].join("|");
    if (seen.has(key) || seen.size >= MAX_PER_LOAD) return;
    seen.add(key);
    send(encode(doc));
  } catch {}
}

// Cuma error dari bundle kita sendiri (origin kita + /static/js/): buang "Script error.", extension
// (termasuk yang CRA-built, punya folder /static/js/ sendiri di chrome-extension://…), tag GTM,
// ResizeObserver. Error axios udah dicatat interceptor httpRequest.js.
const isFirstParty = (e) =>
  e instanceof Error && !e.isAxiosError && (e.stack || "").includes(window.location.origin + "/static/js/");

export function installGlobalErrorLogging() {
  window.addEventListener("error", (e) => isFirstParty(e.error) && logClientError(e.error, "error"));
  window.addEventListener(
    "unhandledrejection",
    (e) => isFirstParty(e.reason) && logClientError(e.reason, "rejection"),
  );
  window.addEventListener("online", flushQueue);
  flushQueue();
}
